import { ApplicationConfig, provideZoneChangeDetection, APP_INITIALIZER, inject } from '@angular/core';
import { provideRouter, withComponentInputBinding } from '@angular/router';
import { provideFirebaseApp, initializeApp } from '@angular/fire/app';
import { provideAuth, getAuth } from '@angular/fire/auth';
import { browserLocalPersistence, setPersistence } from '@angular/fire/auth';
import { provideFirestore } from '@angular/fire/firestore';
import { getApp } from 'firebase/app';
import { initializeFirestore, persistentLocalCache, persistentMultipleTabManager } from 'firebase/firestore';
import { provideStorage, getStorage } from '@angular/fire/storage';
import { provideMessaging, getMessaging } from '@angular/fire/messaging';
import { routes } from './app.routes';
import { environment } from '../environments/environment';
import { provideHttpClient, HTTP_INTERCEPTORS, withInterceptorsFromDi } from '@angular/common/http';
import { provideI18n } from '@shared';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { ErrorHandler } from '@angular/core';
import { GlobalErrorHandler } from '@shared';
import { ApiConfigService, AuthInterceptor, OfflineInterceptor, OfflineCacheService } from '@shared';

// Initialize API configuration
function initializeApiConfig() {
  return () => {
    const apiConfig = inject(ApiConfigService);
    apiConfig.setApiUrl(environment.apiUrl);
    apiConfig.setWsUrl(environment.wsUrl);
    return Promise.resolve();
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes, withComponentInputBinding()),
    provideAnimationsAsync(),
    provideHttpClient(withInterceptorsFromDi()),
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: OfflineInterceptor, multi: true },
    provideI18n(),
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    { provide: APP_INITIALIZER, useFactory: initializeApiConfig, multi: true },
    {
      provide: APP_INITIALIZER,
      useFactory: () => {
        return () => {
          const cacheService = inject(OfflineCacheService);
          cacheService.initialize();
          return Promise.resolve();
        };
      },
      multi: true,
    },
    provideFirebaseApp(() => initializeApp(environment.firebase)),
    provideAuth(() => {
      const auth = getAuth();
      // Set persistence to LOCAL for web - persists across page refreshes
      // For mobile (Capacitor), Firebase Auth automatically uses native storage
      if (typeof window !== 'undefined' && !(window as { Capacitor?: unknown }).Capacitor) {
        setPersistence(auth, browserLocalPersistence).catch(() => {
          // Error will be handled by global error handler
        });
      }
      return auth;
    }),
    provideFirestore(() =>
      initializeFirestore(getApp(), {
        localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager() }),
        experimentalAutoDetectLongPolling: true,
      }),
    ),
    provideStorage(() => getStorage()),
    provideMessaging(() => getMessaging()),
  ],
};
